import { useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { Dropdown } from "components";
import * as hooks from "lib/hooks";
import {
	LpnUploadTemplate,
	UltiumSerialUploadTemplate,
	UploadContentTypeLPN,
	UploadContentTypeSerial,
} from "lib/util/constants";
import * as result from "lib/result";
import * as labels from "pages/Upload/cards/constants";

export interface UploadContentType {
	label: string;
	templateFileName: string;
}

// ASRS-LPN intake options are LPN or serial list only; no Product Number field (SITMP-7232).
export const DefaultUploadContentTypes: UploadContentType[] = [
	{ label: UploadContentTypeLPN, templateFileName: LpnUploadTemplate },
	{
		label: UploadContentTypeSerial,
		templateFileName: UltiumSerialUploadTemplate,
	},
];

interface uploadContentTypeSelectorProps {
	options?: UploadContentType[];
	value?: UploadContentType | null;
	onChange?: (val: UploadContentType | null) => void;
	onDownload?: () => void;
	downloading?: boolean;
	/** @deprecated Use onChange instead. Kept for backward compatibility. */
	onSelect?: (selected: UploadContentType | null) => void;
}

export function UploadContentTypeSelector({
	options,
	value: controlledValue,
	onChange: controlledOnChange,
	onDownload: controlledOnDownload,
	downloading: controlledDownloading,
	onSelect,
}: uploadContentTypeSelectorProps = {}): React.JSX.Element {
	const materialColors = hooks.UseMaterialColors();
	const mccApi = hooks.UseMccApi();
	const snack = hooks.UseSnackBar();

	const [internalValue, setInternalValue] = useState<UploadContentType | null>(null);
	const [internalDownloading, setInternalDownloading] = useState(false);

	const isControlled = controlledValue !== undefined;
	const selected = isControlled ? controlledValue : internalValue;
	const downloading = controlledDownloading ?? internalDownloading;
	const types = options ?? DefaultUploadContentTypes;

	const handleChange = (val: UploadContentType | null) => {
		if (!isControlled) setInternalValue(val);
		controlledOnChange?.(val);
		onSelect?.(val);
	};

	const handleDownload = async () => {
		if (controlledOnDownload) {
			controlledOnDownload();
			return;
		}
		if (!selected) return;
		setInternalDownloading(true);
		try {
			const blob = await mccApi.DownloadExcelTemplate(
				selected.templateFileName
			);
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = selected.templateFileName;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			snack.open(result.Err("Template download failed"));
		} finally {
			setInternalDownloading(false);
		}
	};

	return (
		<Box display="flex" flexDirection="column" gap={1.5}>
			<Typography sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
				{labels.UploadContentType}
			</Typography>
			<Box sx={{ minWidth: 0 }}>
				<Dropdown
					data={types}
					value={selected}
					getOptionLabel={(opt) => opt.label}
					isOptionEqualToValue={(a, b) => a.label === b.label}
					onChange={handleChange}
					fullWidth
					multiple={false}
					label={labels.UploadContentType}
					hideLabel
					textFieldProps={{ placeholder: "Select upload type" }}
					clearable={false}
				/>
			</Box>
			{selected && (
				<Link
					component="button"
					underline="always"
					onClick={handleDownload}
					disabled={downloading}
					sx={{
						alignSelf: "flex-start",
						cursor: downloading ? "wait" : "pointer",
						color: materialColors.primary,
						fontSize: "0.95rem",
					}}
				>
					{downloading ? "Downloading..." : "Download Template"}
				</Link>
			)}
		</Box>
	);
}
